// 自動コメント生成のタイミング制御モジュール

import {
	type AutoCommentConfig,
	DEFAULT_AUTO_COMMENT_CONFIG,
} from "../types/core.js";

/**
 * 自動コメント生成のトリガー判定を担うクラス。
 * 以下の複数シグナルを組み合わせて「今がコメントを生成する適切なタイミングか」を判断する:
 * - 沈黙時間（話の途中でのコメントを防ぐ）
 * - 前回生成からの経過時間（連続生成・生成なし放置を防ぐ）
 * - 前回生成以降の新規テキスト量（「あー」程度の発話での生成を防ぐ）
 */
export class AutoCommentTrigger {
	private config: AutoCommentConfig;

	/** 前回コメントを生成した時刻（null = まだ一度も生成していない） */
	private lastCommentTime: number | null = null;

	/** 前回コメント生成以降に蓄積された新規テキストの文字数 */
	private newCharsCount = 0;

	/** コメント生成中フラグ（二重生成防止） */
	private isGenerating = false;

	/** 生成コールバック */
	private onTrigger: (() => void) | null = null;

	constructor(config: Partial<AutoCommentConfig> = {}) {
		this.config = { ...DEFAULT_AUTO_COMMENT_CONFIG, ...config };
	}

	/**
	 * 設定を更新する
	 */
	updateConfig(config: Partial<AutoCommentConfig>): void {
		this.config = { ...this.config, ...config };
	}

	/**
	 * 現在の設定を取得する
	 */
	getConfig(): AutoCommentConfig {
		return { ...this.config };
	}

	/**
	 * トリガー時に呼ばれるコールバックを登録する
	 */
	onTriggerComment(callback: () => void): void {
		this.onTrigger = callback;
	}

	/**
	 * 新規の確定テキストが追加されたことを通知する。
	 * 前回コメント以降の文字数を追跡する。
	 */
	onNewTranscript(text: string): void {
		this.newCharsCount += text.length;
	}

	/**
	 * 沈黙が検知されたことを通知し、トリガー判定を行う。
	 * AudioAnalyzer の onSpeechSegment イベントから呼ばれることを想定。
	 * @param silenceDurationMs 現在の沈黙時間（ms）
	 */
	onSilenceDetected(silenceDurationMs: number): void {
		if (!this.shouldTrigger(silenceDurationMs)) return;
		this.fire();
	}

	/**
	 * コメント生成が完了したことを通知する。
	 * 生成中フラグをリセットし、タイマーを更新する。
	 */
	onCommentGenerated(): void {
		this.isGenerating = false;
		this.lastCommentTime = Date.now();
		this.newCharsCount = 0;
	}

	/**
	 * コメント生成が失敗したことを通知する。
	 * 生成中フラグのみリセットし、タイマーは更新しない。
	 */
	onCommentFailed(): void {
		this.isGenerating = false;
	}

	/**
	 * セッション開始時にステートをリセットする
	 */
	reset(): void {
		this.lastCommentTime = null;
		this.newCharsCount = 0;
		this.isGenerating = false;
	}

	/**
	 * 現在の状態でコメントをトリガーすべきか判定する。
	 * @param silenceDurationMs 現在の沈黙時間（ms）
	 */
	private shouldTrigger(silenceDurationMs: number): boolean {
		// 自動生成が無効
		if (!this.config.enabled) return false;

		// 生成中は二重生成を防ぐ
		if (this.isGenerating) return false;

		// 沈黙が閾値未満（まだ話している可能性が高い）
		if (silenceDurationMs < this.config.silenceThresholdMs) return false;

		const now = Date.now();
		const timeSinceLastComment =
			this.lastCommentTime !== null ? now - this.lastCommentTime : Infinity;

		// 前回コメントからの最短インターバルを満たしていない（連続生成防止）
		if (timeSinceLastComment < this.config.minIntervalMs) return false;

		// 前回コメント以降の新規テキストが少なすぎる（「あー」などを弾く）
		// ただし maxIntervalMs を超えている場合は強制生成（存在感の維持）
		// 初回生成時は maxIntervalMs による強制生成の対象外とする
		const isInitialComment = this.lastCommentTime === null;
		if (
			this.newCharsCount < this.config.minNewChars &&
			(isInitialComment || timeSinceLastComment < this.config.maxIntervalMs)
		) {
			return false;
		}

		return true;
	}

	/**
	 * トリガーを発火する
	 */
	private fire(): void {
		this.isGenerating = true;
		if (this.onTrigger) {
			this.onTrigger();
		}
	}
}
